import { authService } from './authService';

export interface TokenInfo {
  token: string;
  expiresAt: number;
  issuedAt: number;
  userId: string;
  role: string;
  authProvider?: string;
  profileComplete?: boolean;
  emailVerified?: boolean;
  phoneVerified?: boolean;
}

class TokenService {
  private refreshPromise: Promise<void> | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;

  /**
   * Parse JWT token to extract information
   */
  parseToken(token: string): TokenInfo | null {
    try {
      const base64UrlPayload = token.split('.')[1];
      const base64Payload = base64UrlPayload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedPayload = base64Payload.padEnd(base64Payload.length + (4 - (base64Payload.length % 4)) % 4, '=');
      const payload = JSON.parse(atob(paddedPayload));
      return {
        token,
        expiresAt: payload.exp * 1000, // Convert to milliseconds
        issuedAt: payload.iat * 1000,
        userId: payload.sub || payload.userId || payload._id || payload.id,
        role: payload.role || payload.currentRole,
        authProvider: payload.authProvider,
        profileComplete: payload.profileComplete ?? false,
        emailVerified: payload.emailVerified ?? payload.isEmailVerified ?? false,
        phoneVerified: payload.phoneVerified ?? payload.isPhoneVerified ?? false,
      } as any;
    } catch (error) {
      console.error('Failed to parse token:', error);
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string): boolean {
    const tokenInfo = this.parseToken(token);
    if (!tokenInfo) return true;

    const now = Date.now();
    return now >= tokenInfo.expiresAt;
  }

  /**
   * Check if token will expire soon (within buffer time)
   */
  isTokenExpiringSoon(token: string, bufferMinutes: number = 5): boolean {
    const tokenInfo = this.parseToken(token);
    if (!tokenInfo) return true;

    const now = Date.now();
    const bufferMs = bufferMinutes * 60 * 1000;
    return now >= (tokenInfo.expiresAt - bufferMs);
  }

  /**
   * Get remaining token time in milliseconds
   */
  getRemainingTime(token: string): number {
    const tokenInfo = this.parseToken(token);
    if (!tokenInfo) return 0;

    const now = Date.now();
    return Math.max(0, tokenInfo.expiresAt - now);
  }

  /**
   * Validate token role is fabric-buyer
   */
  validateFabricBuyerToken(token: string): boolean {
    const tokenInfo = this.parseToken(token);
    return tokenInfo?.role === 'fabric-buyer';
  }

  /**
   * Start automatic token refresh
   */
  startAutoRefresh(onTokenRefreshed?: (token: string) => void, onRefreshFailed?: () => void): void {
    if (import.meta.env.VITE_AUTO_REFRESH_TOKEN === 'false') return;

    this.stopAutoRefresh(); // Clear any existing timer

    const checkAndRefresh = async () => {
      try {
        const currentToken = authService.getStoredAuthToken();
        if (!currentToken) {
          this.stopAutoRefresh();
          return;
        }

        // Check if token needs refresh (5 minutes before expiry)
        if (this.isTokenExpiringSoon(currentToken, 5)) {
          await this.refreshToken();
          const newToken = authService.getStoredAuthToken();
          if (newToken && onTokenRefreshed) {
            onTokenRefreshed(newToken);
          }
        }
      } catch (error) {
        console.error('Auto refresh failed:', error);
        if (onRefreshFailed) {
          onRefreshFailed();
        }
        this.stopAutoRefresh();
      }
    };

    // Check every minute
    this.refreshTimer = setInterval(checkAndRefresh, 60000);

    // Initial check
    checkAndRefresh();
  }

  /**
   * Stop automatic token refresh
   */
  stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Refresh token (placeholder - backend endpoint not yet available)
   */
  async refreshToken(): Promise<string> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      await this.refreshPromise;
      return authService.getStoredAuthToken() || '';
    }

    this.refreshPromise = this._performRefresh();
    
    try {
      await this.refreshPromise;
      return authService.getStoredAuthToken() || '';
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Internal refresh implementation
   */
  private async _performRefresh(): Promise<void> {
    const currentToken = authService.getStoredAuthToken();
    if (!currentToken) {
      throw new Error('No token to refresh');
    }

    // TODO: Implement actual refresh endpoint when available
    // For now, this is a placeholder
    console.warn('Token refresh endpoint not yet implemented');
    
    // Simulate refresh for development
    if (import.meta.env.MODE === 'development') {
      console.log('Simulating token refresh in development mode');
      return;
    }

    throw new Error('Token refresh not implemented');
  }

  /**
   * Validate stored token and clean up if invalid
   */
  validateStoredToken(): boolean {
    const token = authService.getStoredAuthToken();
    
    if (!token) {
      return false;
    }

    // Check if expired
    if (this.isTokenExpired(token)) {
      authService.logout();
      return false;
    }

    // Check if fabric buyer role
    if (!this.validateFabricBuyerToken(token)) {
      authService.logout();
      return false;
    }

    return true;
  }

  /**
   * Get token expiry information for display
   */
  getTokenExpiryInfo(token?: string): {
    expiresAt: Date | null;
    remainingTime: string;
    isExpiringSoon: boolean;
  } {
    const currentToken = token || authService.getStoredAuthToken();
    
    if (!currentToken) {
      return {
        expiresAt: null,
        remainingTime: 'No token',
        isExpiringSoon: false,
      };
    }

    const tokenInfo = this.parseToken(currentToken);
    if (!tokenInfo) {
      return {
        expiresAt: null,
        remainingTime: 'Invalid token',
        isExpiringSoon: true,
      };
    }

    const remainingMs = this.getRemainingTime(currentToken);
    const remainingMinutes = Math.floor(remainingMs / (1000 * 60));
    const remainingHours = Math.floor(remainingMinutes / 60);

    let remainingTime: string;
    if (remainingHours > 0) {
      remainingTime = `${remainingHours}h ${remainingMinutes % 60}m`;
    } else if (remainingMinutes > 0) {
      remainingTime = `${remainingMinutes}m`;
    } else {
      remainingTime = 'Expired';
    }

    return {
      expiresAt: new Date(tokenInfo.expiresAt),
      remainingTime,
      isExpiringSoon: this.isTokenExpiringSoon(currentToken),
    };
  }
}

export const tokenService = new TokenService(); 
